import { ExtremeGridInstance, Mobile, ProjectHelper } from '@bimser/common';
import {
	BCButton,
	BCDivider,
	BCExtremeGridEntities,
	BCInput,
	BCMultipleCombobox,
	BCPopover,
	BCSpaceCompact,
	findMessage,
	IButtonClickEventArgs,
	IInputChangeEventArgs
} from '@bimser/components';
import {
	createGuid,
	FormItemTypes,
	IDictionary,
	IItem,
	isNullOrUndefined,
	MLHelper,
	mobile
} from '@bimser/core';
import IconClosePanel from '@bimser/icons/16/close-panel';
import CloseIcon from '@bimser/icons/16/close-window';
import IconMore from '@bimser/icons/16/more2';
import SaveIcon from '@bimser/icons/16/save';
import { Plugins } from '@bimser/studio-core';
import dayjs from 'dayjs';
import { Map } from 'immutable';
import { Liquid } from 'liquidjs';
import cloneDeep from 'lodash/cloneDeep';
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';
import { JSX } from 'react';
import { Portal } from 'react-portal';
import { FormControlsHelpers } from '../../../..';
import { IDataGridColumnCollection } from '../../../../entities';
import {
	HorizontalAlign,
	IDataSource,
	IOnLoadArgs,
	IPropertyField,
	LookupShowType,
	MultiLanguageText,
	PrimitiveType,
	SizeType
} from '../../../../entities/common';
import { convertTypeToFieldType } from '../../../../entities/common/IPropertyField';
import { IUserEvent } from '../../../../entities/services';
import { getDataSourceParams } from '../../../../helper';
import { getActionValue } from '../../../../sagas/ruleValidation/action';
import InputControl from '../../../runtime/base/InputControl';
import Control from '../../base/Control';
import { ILookupButtonClickEventArgs, IPropertyChangedEventArgs } from '../../entities/EventArgs';
import { DataGridSelectionMode, IDataGridRowCollection } from '../DataGrid/entities';
import ILookupProperties from '././entities/ILookupProperties';
import Styles from './assets/styles.scss';
import LookupGrid from './components/LookupGrid';
import { ILookupGridOptions } from './entities/ILookupGridOptions';
import { filteringStaticForMobile } from './helpers/filteringStaticForMobile';

const pickerPopupId = 'lookupPickerPopupId';

const liquidEngine = new Liquid();

const inputStyle: React.CSSProperties = {
	width: 'calc(100% - 35px)',
	paddingTop: '0px',
	paddingBottom: '0px'
};
const buttonStyle: React.CSSProperties = { width: '35px', fontWeight: 900, fontSize: '14px' };
const inputAllowClearStyle: React.CSSProperties = {
	width: 'calc(100% - 70px)',
	paddingTop: '0px',
	paddingBottom: '0px'
};
const transitionReset: React.CSSProperties = { transition: 'all 0.3s, width 0s' };

export default class Lookup extends InputControl<any[]> {
	dataSource: IDataSource;
	rows: IDataGridRowCollection;
	gridOptions: ILookupGridOptions;
	allowClear: boolean;
	sizeType: SizeType;
	valueType: PrimitiveType;
	text: string;
	selectedItems: Array<any> = [];
	placeholder: MultiLanguageText;
	textAlign: HorizontalAlign;
	showOn: LookupShowType;
	showOnVisible: boolean;
	tempItems: Array<any> = [];
	nonSelectedItems: Array<any> = [];
	isNonSelectedItem: boolean = false;
	__isFirstLoad: boolean = true;
	__extremeGridInstance: ExtremeGridInstance;
	__pageIndex: number = 1;
	__filtering: Array<BCExtremeGridEntities.IFilterItem> = [];
	__sorting: Array<BCExtremeGridEntities.ISortingColumn> = [];
	__searchValue: string;
	__showOnVisible: boolean;
	__dataGridRef: any;
	__isSelectedItem: boolean;
	__triggerDataSourceList: IDictionary<string> = {};
	__commitIdFixed: boolean = false;

	constructor() {
		super();

		this.controlType = FormItemTypes.EControlTypes.Lookup;
		this.dataSource = {
			name: '',
			valueExpr: '',
			displayExpr: [],
			displayFormat: '',
			runAtServer: false,
			parameters: [],
			useClientCachedData: true
		};
		this.rows = [];
		this.gridOptions = {
			paging: {
				enabled: true,
				currentPage: 1,
				pageSize: 10,
				pagingSizes: [10, 20, 30, 40]
			},
			searching: {
				enabled: false
			},
			sorting: {
				enabled: false,
				allSortingColumns: true,
				mode: 'none'
			},
			columns: [],
			columnSettings: {
				orderable: false,
				resizeable: false,
				showColumnChooser: false
			},
			filtering: {
				enabled: false
			},
			selection: {
				mode: 'single'
			}
		};
		this.allowClear = false;
		this.valueType = 'string';
		this.text = '';
		this.showOn = 'modal';
		this.showOnVisible = false;
		this.__isSelectedItem = false;
	}

	__getMLClassPath() {
		return [...super.__getMLClassPath(), 'placeholder'];
	}

	initialize() {
		super.initialize();

		this.placeholder = new MultiLanguageText(
			(text: string, culture: string, existCultures: string[]) => {
				this.__onChangeMLTextValue(text, 'placeholder', culture, existCultures);
			}
		);
		this.textAlign = 'left';

		this.getRows = this.getRows.bind(this);
		this.__onClearButtonClick = this.__onClearButtonClick.bind(this);
		this.__setExtremeGridInstance = this.__setExtremeGridInstance.bind(this);
		this.getSelectedItems = this.getSelectedItems.bind(this);
		this.getSelectedItemsLoadOptions = this.getSelectedItemsLoadOptions.bind(this);
		this.openModalDataGrid = this.openModalDataGrid.bind(this);
		this.openDrawerDataGrid = this.openDrawerDataGrid.bind(this);
		this.openPopoverDataGrid = this.openPopoverDataGrid.bind(this);
		this.__renderInput = this.__renderInput.bind(this);
		this.getLoadOptions = _debounce(this.getLoadOptions.bind(this), 200);
		this.getLookupItems = this.getLookupItems.bind(this);
		this.onSelectedChange = this.onSelectedChange.bind(this);
		this.getDataSourceControl = this.getDataSourceControl.bind(this);
		this.__renderLookupButton = this.__renderLookupButton.bind(this);

		this.subscribe('trigger:OnValueChanged', (data: any) => {
			this.triggerSelectedChanged(JSON.parse(data));
			Mobile.MobileView.setControlProperties(
				this.name,
				this.__getComponentData(),
				this.__form?.__windowId || ''
			);
		});

		this.subscribe('trigger:OnSelectionChange', (data: any) => {
			this.triggerSelectedChanged(JSON.parse(data));
			Mobile.MobileView.setControlProperties(
				this.name,
				this.__getComponentData(),
				this.__form?.__windowId || ''
			);
		});

		this.subscribe('trigger:OnLoadLookupData', () => {
			this.__pageIndex = this.gridOptions.paging.currentPage;
			this.__searchValue = '';
			this.openLookupShow({} as any);
		});

		this.subscribe('trigger:OnPageIndexChange', (value: string) => {
			this.__pageIndex = JSON.parse(value);
			this.getRows(this.__getLoadOptions());
			Mobile.MobileView.setControlProperties(
				this.name,
				this.__getComponentData(),
				this.__form?.__windowId || ''
			);
		});

		this.subscribe('trigger:OnSortingChange', (value: string) => {
			this.__extremeGridInstance?.sort(JSON.parse(value));
		});

		this.subscribe('trigger:OnSearchingChange', (value: string) => {
			this.__searchValue = value;
			this.getRows(this.__getLoadOptions());
			Mobile.MobileView.setControlProperties(
				this.name,
				this.__getComponentData(),
				this.__form?.__windowId || ''
			);
		});

		this.subscribe('trigger:OnFilteringChange', (value: string) => {
			const _columns: IDataGridColumnCollection = JSON.parse(value);

			_columns.forEach(_column => {
				if ((_column as any).sortChanged) {
					const sort: any = _column.sorting;
					sort.direction = sort.order;
					delete sort.order;
					delete sort.enabled;
					sort['columnName'] = _column.name;
					this.__sorting = [sort];
				}
				if ((_column as any).filterChanged) {
					const filterItem: any = _column.filtering;
					delete filterItem.enabled;
					filterItem['values'] = [];
					filterItem['columnName'] = _column.name;

					if (_column.editType === 'numberBox') {
						if (_column.filtering.operation === ('contains' as any)) {
							filterItem['operation'] = 'equal';
						}
						filterItem['value'] = _column.filtering.value.length
							? parseInt(_column.filtering.value)
							: _column.filtering.value;
					}
					if (
						_column.editType === 'datePicker' ||
						_column.editType === 'dateTimePicker' ||
						_column.editType === 'timePicker'
					) {
						if (_column.filtering.operation === ('contains' as any)) {
							filterItem['operation'] = 'greaterThan';
						}
					}

					if (this.__filtering.find(x => x.columnName === filterItem.columnName)) {
						this.__filtering = this.__filtering?.map(filter => {
							if (filter.columnName === filterItem.columnName) {
								return filterItem;
							}
							return filter;
						});
					} else {
						this.__filtering.push(filterItem);
					}
				}

				return _column;
			});

			this.__extremeGridInstance?.customizeRequestData(this.__getLoadOptions());
			this.getRows(this.__getLoadOptions());
			Mobile.MobileView.setControlProperties(
				this.name,
				this.__getComponentData(),
				this.__form?.__windowId || ''
			);
		});
	}

	__loadProperties(data: ILookupProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.dataSource = data.dataSource;
		this.__proxyChanged.gridOptions = data.gridOptions;
		this.__proxyChanged.allowClear = data.allowClear;
		this.__proxyChanged.sizeType = data.sizeType;
		this.__proxyChanged.valueType = data.valueType;
		this.__proxyChanged.text = data.text;
		this.__proxyChanged.textAlign = data.textAlign;
		this.__proxyChanged.selectedItems = data.selectedItems;
		this.__proxyChanged.showOn = data.showOn;
		this.__proxyChanged.showOnVisible = data.showOnVisible;
		this.__proxyChanged.tempItems = data.tempItems || [];
		this.__proxyChanged.rows = data.rows;

		if (!this.isNonSelectedItem) {
			this.getLoadOptions(true);
			this.isNonSelectedItem = true;
		}
		if (data.placeholder) {
			this.placeholder.setData(data.placeholder);
		}
		if (
			data.value?.length &&
			data.dataSource?.name &&
			this.__form.serviceUrl &&
			!data.selectedItems?.length &&
			!this.__isSelectedItem
		) {
			this.__isSelectedItem = true;
			this.getSelectedItems();
		}
	}

	__onLoad({ parameterChanged }: IOnLoadArgs) {
		if (parameterChanged) {
			this.clearValue();
		}

		if (this.dataSource?.parameters?.length) {
			this.dataSource.parameters.forEach(param => {
				if (param?.valueModifier == 'fieldSelection') {
					const fieldArr = param.field.split('.');
					const controlId = fieldArr.shift();
					const propertyPath = fieldArr.join('.');
					if (!this.__triggerDataSourceList[`${controlId}`]) {
						this.__triggerDataSourceList[`${controlId}`] = 'Subscribed';

						(this.__form[controlId] as Control)?.subscribe(
							'event:OnPropertyChanged',
							(args: IPropertyChangedEventArgs<any>) => {
								if (propertyPath === args.name) {
									this.getLoadOptions(true);
								}
							}
						);

						// #endregion SUBSCRIBE TO ON PARAM CHANGE : END
						(this.__form[controlId] as Control)?.subscribe('event:OnDataLoad', () => {
							this.getLoadOptions(true);
						});
					}
				}
			});
		}
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields();

		return [
			...fields,
			{
				name: 'value',
				type: 'array'
			},
			{
				name: 'text',
				type: 'string'
			}
		];
	}

	__getSearchValueFilters(filters: any) {
		const searchFilters: any = { Filters: [], Operator: 2 };

		this.gridOptions.columns.forEach(column => {
			if (
				column?.dbType?.type === 'string' ||
				column?.dbType?.type === 'decimal' ||
				column.editType === 'multiLanguageTextBox'
			) {
				const filterIndex = filters.findIndex((x: any) => x.Column == column.name);
				const culture = MLHelper.getSystemCulture().culture;

				const searchFilter: BCExtremeGridEntities.ILoadFilterItem = {
					Column:
						column.editType === 'multiLanguageTextBox'
							? column.name + '.' + culture
							: column.name,
					ItemType: BCExtremeGridEntities.ItemType.String,
					Not: false,
					Type: BCExtremeGridEntities.FilterType.Contain,
					Value: this.__searchValue
				};

				if (column.dbType.type === 'decimal') {
					const parsedValue = Number(this.__searchValue);

					if (Number.isInteger(parsedValue)) {
						searchFilter.ItemType = BCExtremeGridEntities.ItemType.Int32;
						searchFilter.Type = BCExtremeGridEntities.FilterType.Equal;
						searchFilter.Value = parsedValue;
					}
				}

				if (filterIndex > -1) {
					filters[filterIndex] = {
						Filters: [filters[filterIndex], searchFilter],
						Operator: 2
					} as any;
				} else {
					searchFilters.Filters.push(searchFilter);
				}
			}
		});
		filters = [
			{
				Filters: [...filters, searchFilters],
				Operator: 1
			}
		];

		return filters;
	}

	__getLoadOptions(): BCExtremeGridEntities.ILoadOptions {
		let filters: any = this.__filtering
			.filter(x => !isNullOrUndefined(x.value) && x.value != '')
			.map(x => ({
				Column: x.columnName,
				ItemType: this.__dataTypeToFilterType(
					this.gridOptions.columns.find(i => i.name == x.columnName)?.dbType?.type
				),
				Not: x.operation.includes('not'),
				Type: this.__operationToFilterType(x.operation),
				Value: x.value
			}));
		if (this.__searchValue) {
			filters = this.__getSearchValueFilters(filters);
		}
		return {
			filters: filters,
			pagination: {
				skip: (this.gridOptions.paging.currentPage - 1) * this.gridOptions.paging.pageSize,
				take: this.gridOptions.paging.pageSize
			},
			sorts: this.__sorting.map(x => ({
				Column: x.columnName,
				Type:
					x.direction == 'asc'
						? BCExtremeGridEntities.SortOrder.Asc
						: BCExtremeGridEntities.SortOrder.Desc
			}))
		};
	}

	__operationToFilterType(
		operation: BCExtremeGridEntities.FilterOperations
	): BCExtremeGridEntities.FilterType {
		switch (operation) {
			case 'contains':
				return BCExtremeGridEntities.FilterType.Contain;
			case 'endsWith':
				return BCExtremeGridEntities.FilterType.Contain;
			case 'equal':
				return BCExtremeGridEntities.FilterType.Equal;
			case 'graterThenOrEqual':
				return BCExtremeGridEntities.FilterType.GreaterOrEqual;
			case 'greaterThan':
				return BCExtremeGridEntities.FilterType.Greater;
			case 'lessThan':
				return BCExtremeGridEntities.FilterType.Less;
			case 'lessThanOrEqual':
				return BCExtremeGridEntities.FilterType.LessOrEqual;
			case 'notContains':
				return BCExtremeGridEntities.FilterType.Contain;
			case 'notEqual':
				return BCExtremeGridEntities.FilterType.Equal;
			case 'startsWith':
				return BCExtremeGridEntities.FilterType.Contain;
			default:
				return BCExtremeGridEntities.FilterType.Contain;
		}
	}

	__dataTypeToFilterType(type: string): BCExtremeGridEntities.ItemType {
		switch (type) {
			case 'boolean':
				return BCExtremeGridEntities.ItemType.Boolean;
			case 'date':
				return BCExtremeGridEntities.ItemType.DateTime;
			case 'dateTime':
				return BCExtremeGridEntities.ItemType.DateTimeOffset;
			case 'decimal':
			case 'int':
				return BCExtremeGridEntities.ItemType.Int32;
			case 'string':
				return BCExtremeGridEntities.ItemType.String;
		}
	}

	private clearValue() {
		this.__proxyChanging.value = null;
		this.__proxyChanging.text = null;
		this.__proxyChanging.selectedItems = [];
	}

	private onClear(args: IInputChangeEventArgs) {
		if (!args.data) {
			this.clearValue();
		}
	}

	private getParamFields() {
		const fieldParams: IDictionary<any> = {};

		if (this.dataSource && this.dataSource.parameters && this.dataSource.parameters.length) {
			this.dataSource.parameters.forEach(p => {
				const field = p.field.split('.');
				const controlId = field[0];
				const propPath = field.slice(1, field.length).join('.');
				fieldParams[p.field] = getActionValue(controlId, propPath, this.__form.items);
			});
		}

		return fieldParams;
	}

	private getValueForText(value: any, selectedRow: any) {
		const valueExpr = this.dataSource.valueExpr;
		const foundObject = selectedRow.cells?.find(cell => cell.name === valueExpr);
		const foundValue = !isNullOrUndefined(selectedRow[valueExpr])
			? selectedRow[valueExpr]
			: foundObject?.value;

		if (isNullOrUndefined(value)) {
			value = [foundValue];
		} else if (Array.isArray(value)) {
			value = [...value, foundValue];
		} else {
			value = [value, foundValue];
		}

		return value;
	}

	private getFormattedText(displayFormat: string, selectedRow: any, text: string) {
		return text === ''
			? liquidEngine.parseAndRenderSync(displayFormat, selectedRow)
			: `${text},${liquidEngine.parseAndRenderSync(displayFormat, selectedRow)}`;
	}

	private getRowsForCells(value: any) {
		const cellObj = {};
		for (const cell of value.cells) {
			cellObj[cell.name] = cell.value;
		}
		return cellObj;
	}

	private getSelectedRowsForColumns(selectedRows: any[]) {
		const columns = this.__getColumns().map(item => item.name);

		return selectedRows.map(item =>
			columns.reduce((newItem, key) => {
				newItem[key] = item[key];
				return newItem;
			}, {})
		);
	}

	triggerSelectedChanged(selectedRows: any[]) {
		if (selectedRows?.length) {
			let value: any;
			let text = '';

			for (const selectedRow of selectedRows) {
				if (Object.keys(selectedRow).length > 0) {
					let displayFormat = this.dataSource.displayFormat;

					if (displayFormat.includes('.__userCulture')) {
						displayFormat = displayFormat.replace(
							'.__userCulture',
							`['${this.__form.currentLanguage.culture}']`
						);
					}

					const selectedRowForText = !!selectedRow.cells
						? this.getRowsForCells(selectedRow)
						: selectedRow;

					text = this.getFormattedText(displayFormat, selectedRowForText, text);
					value = this.getValueForText(value, selectedRow);
				}
			}

			this.__proxyChanging.selectedItems = this.getSelectedRowsForColumns(selectedRows);
			this.__form.applyChanges().then(() => {
				const textML = new MultiLanguageText(
					(mlTextValue: string, culture: string, existCultures: string[]) => {
						this.__onChangeMLTextValue(mlTextValue, 'text', culture, existCultures);
					},
					text,
					this.__form.currentLanguage.culture
				);
				this.__proxyChanging.value = value;
				this.__proxyChanging.text = textML.getText();
			});
		} else if (!selectedRows?.length) {
			this.__proxyChanging.value = null;
			this.__proxyChanging.text = null;
			this.__proxyChanging.selectedItems = [];
		}
	}

	getDataSourceData(loadOptions: BCExtremeGridEntities.ILoadOptions) {
		const paramArgs = getDataSourceParams(this.dataSource, this.getParamFields());
		if (paramArgs.isValid) {
			return ProjectHelper.RunPromiseFormService({
				projectName: this.__form.projectName,
				formName: 'DataSource',
				serviceName: this.dataSource.name,
				requestParameters: {
					...paramArgs.requestPayload,
					loadOptions: {
						...loadOptions,
						filterNulls: true,
						distinct: true
					}
				},
				requestConfig: {
					headers: {
						'Content-Type': 'application/json; charset=utf-8'
					},
					cache: false,
					useClientCachedData: this.dataSource.useClientCachedData,
					clientCacheId: this.__form.__windowId
				}
			});
		}

		return Promise.reject();
	}

	__setExtremeGridInstance(grid: ExtremeGridInstance) {
		this.__extremeGridInstance = grid;
	}

	__updatePaginationByPlatform(loadOptions: BCExtremeGridEntities.ILoadOptions) {
		if (
			this.__form.__fromMobile &&
			this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus &&
			!loadOptions.filters.length
		) {
			loadOptions.pagination = {
				skip: (this.__pageIndex - 1) * this.gridOptions.paging.pageSize,
				take: this.gridOptions.paging.pageSize
			};
		}
		return loadOptions;
	}

	__updateFiltersByItemType(loadOptions: BCExtremeGridEntities.ILoadOptions) {
		loadOptions.filters.forEach(item => {
			if ('Filters' in item) {
				item.Filters.forEach(filter => {
					if (!('Filters' in filter)) {
						if (filter.ItemType === BCExtremeGridEntities.ItemType.DateTime) {
							filter.ItemType = BCExtremeGridEntities.ItemType.DateTimeOffset;

							if (
								filter.Type == this.__operationToFilterType('lessThanOrEqual') ||
								filter.Type == this.__operationToFilterType('greaterThan')
							) {
								filter.Value = new Date(new Date(filter.Value).setSeconds(59));
							}
						}
					}
				});
			} else {
				if (item.ItemType === BCExtremeGridEntities.ItemType.DateTime) {
					item.ItemType = BCExtremeGridEntities.ItemType.DateTimeOffset;
					if (
						item.Type == this.__operationToFilterType('lessThanOrEqual') ||
						item.Type == this.__operationToFilterType('greaterThan')
					) {
						item.Value = new Date(new Date(item.Value).setSeconds(59));
					}
				}
			}
		});

		return loadOptions;
	}

	mobileControlDataCount(count: number) {
		if (
			(Mobile.MobileView.isAndroidAvaliable || Mobile.MobileView.isIosAvaliable) &&
			this.__isFirstLoad &&
			count == 0
		) {
			this.gridOptions.filtering.enabled = false;
			this.gridOptions.searching.enabled = false;
			this.__isFirstLoad = false;
		}
	}

	getRows(loadOptions: BCExtremeGridEntities.ILoadOptions, ignoreMobile?: boolean) {
		return new Promise<Plugins.QueryDesigner.Entities.Bases.IDataSourceResponse>(
			(resolve, reject) => {
				if (
					this.dataSource &&
					!this.dataSource.runAtServer &&
					this.dataSource.name &&
					this.__form.serviceUrl
				) {
					loadOptions = this.__updatePaginationByPlatform(loadOptions);
					loadOptions = this.__updateFiltersByItemType(loadOptions);

					this.getDataSourceData(loadOptions)
						.then(response => {
							const data = FormControlsHelpers.getResultData(response.data);
							const uniqDataFieldName = this.gridOptions.columns.find(
								column => column.isPrimaryKey
							)?.name;

							if (!ignoreMobile) {
								Mobile.MobileView.setFormLookupRows({
									controlId: this.name,
									rows: response.data?.result,
									count: response.data?.count,
									pageIndex: this.__pageIndex
								});
							}

							if (Array.isArray(data) && data?.length) {
								const rows: any[] = [];

								data.forEach((row: any) => {
									const newRow: IDictionary<any> = {};

									Object.keys(row).forEach(columnKey => {
										newRow[columnKey] = _get(row, columnKey.split('.'), null);
									});

									if (
										!rows.find(
											existingRow =>
												existingRow[uniqDataFieldName] ===
												newRow[uniqDataFieldName]
										)
									) {
										rows.push(newRow);
									}
								});

								response.data.result = rows;
								this.mobileControlDataCount(response.data.count);
								resolve(response.data);
							}
							resolve(response.data);
						})
						.catch(() => {
							reject();
						});
				} else if (this.dataSource.runAtServer && mobile().any()) {
					const rowsMobile = this.rows.filter(row => filteringStaticForMobile(this, row));
					this.mobileControlDataCount(rowsMobile.length);
					Mobile.MobileView.setFormLookupRows({
						controlId: this.name,
						rows: rowsMobile,
						count: rowsMobile.length,
						pageIndex: this.__pageIndex
					});
				} else {
					reject();
				}
			}
		);
	}

	getSelectedItemsLoadOptions() {
		const column = this.gridOptions.columns.find(i => i.name == this.dataSource?.valueExpr);
		const filterList: any = this.value?.map(x => ({
			Column: this.dataSource?.valueExpr || column?.name,
			ItemType: this.__dataTypeToFilterType(column?.dbType?.type || this.valueType),
			Not: false,
			Type: BCExtremeGridEntities.FilterType.Equal,
			Value: x
		}));

		return {
			filters: [
				{
					Filters: filterList,
					Operator: 2
				}
			],
			pagination: { skip: 0, take: this.value?.length },
			sorts: []
		};
	}

	getSelectedItems() {
		const loadOptions = this.getSelectedItemsLoadOptions();
		this.getDataSourceData(loadOptions)
			.then(response => {
				const data = FormControlsHelpers.getResultData(response.data);
				const uniqDataFieldName = this.gridOptions.columns.find(
					column => column.isPrimaryKey
				)?.name;

				if (Array.isArray(data) && data?.length) {
					const rows: any[] = [];

					data.forEach((row: any) => {
						const newRow: IDictionary<any> = {};

						Object.keys(row).forEach(columnKey => {
							newRow[columnKey] = _get(row, columnKey.split('.'), null);
						});

						if (
							!rows.find(
								existingRow =>
									existingRow[uniqDataFieldName] === newRow[uniqDataFieldName]
							)
						) {
							rows.push(newRow);
						}
					});

					this.__proxyChanging.selectedItems = rows;

					this.__form.applyChanges().then(() => {
						this.__isSelectedItem = false;
					});
				}
			})
			.catch(() => {
				this.__isSelectedItem = false;
			});
	}

	private openModalDataGrid(args: IButtonClickEventArgs) {
		if (this.__form.serviceUrl) {
			let _selectedRows: Array<any>;
			const messageBoxId = createGuid() + '_lookupGrid'; //messageBoxId ifadesini mobil tarafta sağlıklı kapatabilmek için "_lookupGrid" ekledik.

			const argument: ILookupButtonClickEventArgs = {};
			this.emit<IUserEvent<ILookupButtonClickEventArgs>>('event:server:OnLookupButtonClick', {
				argument,
				controlId: this.name,
				name: 'OnLookupButtonClick',
				sender: 'web'
			}).catch(err => {
				console.log(
					`An error occurred on ${this.name} event:server:OnLookupButtonClick `,
					err
				);
			});
			this.emit<ILookupButtonClickEventArgs>('event:OnLookupButtonClick', argument);

			if (!this.clientEvents.find(x => x.name === 'OnLookupButtonClick')) {
				this.__form.__openedModalList.push(messageBoxId);

				const onSave = () => {
					if (
						!(
							this.__form.__fromMobile &&
							this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
						)
					) {
						_selectedRows && this.triggerSelectedChanged(_selectedRows);
					} else if (
						this.__form.__fromMobile &&
						this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
					) {
						this.__pageIndex = this.gridOptions.paging.currentPage;
					}

					close();
				};

				const { close } = this.__form.__interactionHelper({
					id: messageBoxId,
					type: 'modal',
					title: this.caption.text.getText(),
					width: 'large',
					contentComponentType: 'LookupGrid',
					contentComponentProps: {
						messageBoxId: messageBoxId,
						onRowSelect: selectedRow => {
							_selectedRows = selectedRow;
						},
						onRowDoubleClick: onSave,
						gridOptions: this.gridOptions,
						dataSource: this.dataSource,
						controlId: this.controlId,
						currentLanguage: this.__form.currentLanguage,
						supportedLanguages: this.__form.supportedLanguages,
						selectedValues: this.value,
						selectedItems: this.selectedItems,
						getRows: this.getRows,
						setExtremeGridInstance: this.__setExtremeGridInstance,
						height: window.innerHeight - 315,
						rows: this.rows,
						gridId: `runtime_${this.__form.projectName}_${this.__form.formName}_${this.__form.activeView}_${this.name}`,
						imageModalClassName: `${this.__form.projectName}_${this.__form.formName}_${this.name}`
					},
					onOk: () => {
						onSave();
					}
				});
			}
		}

		args?.event?.stopPropagation();
	}

	private openDrawerDataGrid() {
		let _selectedRows: Array<any>;
		const drawerId = createGuid() + '_lookupGrid';
		const onSave = () => {
			if (
				!(
					this.__form.__fromMobile &&
					this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
				)
			) {
				_selectedRows && this.triggerSelectedChanged(_selectedRows);
			} else if (
				this.__form.__fromMobile &&
				this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
			) {
				this.__pageIndex = this.gridOptions.paging.currentPage;
			}
			close();
		};

		const { close } = this.__form.__interactionHelper({
			id: drawerId,
			type: 'drawer',
			title: this.caption.text.getText(),
			width: '800px',
			contentComponentType: 'LookupGrid',
			contentComponentProps: {
				messageBoxId: drawerId,
				onRowSelect: selectedRow => {
					_selectedRows = selectedRow;
				},
				onRowDoubleClick: onSave,
				gridOptions: this.gridOptions,
				dataSource: this.dataSource,
				controlId: this.controlId,
				currentLanguage: this.__form.currentLanguage,
				supportedLanguages: this.__form.supportedLanguages,
				selectedValues: this.value,
				selectedItems: this.selectedItems,
				getRows: this.getRows,
				setExtremeGridInstance: this.__setExtremeGridInstance,
				height: '100%',
				rows: this.rows,
				gridId: `runtime_${this.__form.projectName}_${this.__form.formName}_${this.__form.activeView}_${this.name}`,
				imageModalClassName: `${this.__form.projectName}_${this.__form.formName}_${this.name}`
			},
			onOk: () => {
				onSave();
			}
		});
	}

	private openPopoverDataGrid() {
		let _selectedRows: Array<any>;
		const popoverId = createGuid() + '_lookupGrid';

		const onClose = () => {
			this.__proxyChanging.showOnVisible = false;
		};

		const onSave = () => {
			if (
				!(
					this.__form.__fromMobile &&
					this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
				)
			) {
				_selectedRows && this.triggerSelectedChanged(_selectedRows);
			} else if (
				this.__form.__fromMobile &&
				this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
			) {
				this.__pageIndex = this.gridOptions.paging.currentPage;
			}
			onClose();
		};

		const onRowDoubleClick = () => {
			onSave();
		};

		const overlayClassName = [Styles.lookupPopover];

		if (this.__form.__fromMobile) {
			overlayClassName.push(Styles.lookupPopoverWithWidthMobile);
		} else {
			overlayClassName.push(Styles.lookupPopoverWithWidth);
		}

		return (
			<BCPopover
				trigger="click"
				overlayClassName={overlayClassName.join(' ')}
				open={this.showOnVisible}
				zIndex={1000}
				placement="bottomRight"
				autoAdjustOverflow
				destroyTooltipOnHide
				getPopupContainer={this.__form.__getShadowRootContainer}
				title={
					<div className={Styles.drawerFormHeader}>
						<div className={Styles.drawerFormTitle}>{this.caption.text.getText()}</div>
						<div className={Styles.drawerHeaderButtons}>
							<BCButton
								className={Styles.saveButton}
								icon={<SaveIcon />}
								onClick={onSave}
							/>
							<BCDivider
								className={Styles.drawerHeaderDivider}
								type="vertical"
							/>
							<CloseIcon
								className={Styles.closePanel}
								onClick={onClose}
							/>
						</div>
					</div>
				}
				content={
					<LookupGrid
						messageBoxId={popoverId}
						onRowSelect={selectedRow => {
							_selectedRows = selectedRow;
						}}
						onRowDoubleClick={onRowDoubleClick}
						gridOptions={this.gridOptions}
						dataSource={this.dataSource}
						controlId={this.controlId}
						currentLanguage={this.__form.currentLanguage}
						supportedLanguages={this.__form.supportedLanguages}
						selectedValues={this.value}
						selectedItems={this.selectedItems}
						getRows={this.getRows}
						rows={this.rows}
						setExtremeGridInstance={this.__setExtremeGridInstance}
						height="300px"
						gridId={`runtime_${this.__form.projectName}_${this.__form.formName}_${this.__form.activeView}_${this.name}`}
						onClose={onClose}
						imageModalClassName={`${this.__form.projectName}_${this.__form.formName}_${this.name}`}
					/>
				}
			>
				<BCButton
					style={{ ...buttonStyle, ...{ backgroundColor: this.style.backgroundColor } }}
					onClick={this.openLookupShow.bind(this)}
					size={this.sizeType}
					icon={<IconMore />}
					disabled={this.__isDisabled()}
				/>
			</BCPopover>
		);
	}

	private openLookupShow(args: IButtonClickEventArgs) {
		if (this.__isDisabled() || this.__isReadOnly()) {
			return;
		}

		switch (this.showOn) {
			case 'drawer':
			case 'popover':
				if (this.__form.serviceUrl) {
					const argument: ILookupButtonClickEventArgs = {};
					this.emit<IUserEvent<ILookupButtonClickEventArgs>>(
						'event:server:OnLookupButtonClick',
						{
							argument,
							controlId: this.name,
							name: 'OnLookupButtonClick',
							sender: 'web'
						}
					).catch(err => {
						console.log(
							`An error occurred on ${this.name} event:server:OnLookupButtonClick `,
							err
						);
					});
					this.emit<ILookupButtonClickEventArgs>('event:OnLookupButtonClick', argument);

					if (!this.clientEvents.find(x => x.name === 'OnLookupButtonClick')) {
						this.__proxyChanging.showOnVisible = true;

						if (
							this.showOn === 'drawer' ||
							(this.__form.isMobile() && this.showOn === 'popover')
						) {
							this.openDrawerDataGrid();
						}
					}
				}
				break;
			default:
				this.openModalDataGrid(args);
				break;
		}
	}

	__hasValue() {
		return (
			(!isNullOrUndefined(this.text) && this.text != '') ||
			(!isNullOrUndefined(this.value) && this.value.length)
		);
	}

	__onClearButtonClick() {
		if (!(this.__isDisabled() || this.__isReadOnly())) {
			this.__proxyChanging.value = null;
			this.__proxyChanging.text = null;
			this.__proxyChanging.selectedItems = [];
		}
	}

	__renderLookupButton() {
		if (this.showOn == 'popover' && !this.__form.isMobile()) {
			return this.openPopoverDataGrid();
		}

		return (
			<BCButton
				style={{
					...buttonStyle,
					...{ backgroundColor: this.style.backgroundColor }
				}}
				onClick={this.openLookupShow.bind(this)}
				icon={<IconMore />}
				disabled={this.__isDisabled() || this.__isReadOnly()}
			/>
		);
	}

	__renderButtons() {
		return (
			<>
				{this.__form.__fromMobile &&
				this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus &&
				this.__hasValue() &&
				this.allowClear ? (
					<BCButton
						className={Styles.clearButtonStyles}
						style={{
							...buttonStyle,
							...{ backgroundColor: this.style.backgroundColor }
						}}
						onClick={this.__onClearButtonClick}
						icon={<IconClosePanel />}
						disabled={this.__isDisabled() || this.__isReadOnly()}
					/>
				) : null}

				{this.__renderLookupButton()}
			</>
		);
	}

	__getInputStyle() {
		let customStyles: React.CSSProperties;

		if (
			this.__form.__fromMobile &&
			this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus &&
			this.__hasValue() &&
			this.allowClear
		) {
			customStyles = { ...inputAllowClearStyle };
		} else {
			customStyles = { ...inputStyle };
		}
		customStyles.textAlign = this.textAlign;

		return { ...customStyles, ...this.style, ...transitionReset };
	}

	__getColumns() {
		return this.gridOptions.columns.map(column => {
			const filterOperations: any = {};

			switch (column?.editType) {
				case 'numberBox': {
					filterOperations['notContains'] = {
						text: findMessage.get('101719'),
						name: 'notContains',
						symbol: 'notContains'
					};
					filterOperations['equal'] = {
						text: findMessage.get('101711'),
						name: 'equal',
						symbol: '='
					};
					filterOperations['notEqual'] = {
						text: findMessage.get('101712'),
						name: 'notEqual',
						symbol: '<>'
					};
					filterOperations['greaterThan'] = {
						text: findMessage.get('101713'),
						name: 'greaterThan',
						symbol: '>'
					};
					filterOperations['graterThenOrEqual'] = {
						text: findMessage.get('101714'),
						name: 'graterThenOrEqual',
						symbol: '>='
					};
					filterOperations['lessThan'] = {
						text: findMessage.get('101715'),
						name: 'lessThan',
						symbol: '<'
					};
					filterOperations['lessThanOrEqual'] = {
						text: findMessage.get('101716'),
						name: 'lessThanOrEqual',
						symbol: '=<'
					};
					break;
				}
				case 'checkBox': {
					filterOperations['contains'] = {
						text: findMessage.get('101718'),
						name: 'contains',
						symbol: 'contains'
					};
					filterOperations['notContains'] = {
						text: findMessage.get('101719'),
						name: 'notContains',
						symbol: 'notContains'
					};
					filterOperations['startsWith'] = {
						text: findMessage.get('101720'),
						name: 'startsWith',
						symbol: 'startsWith'
					};
					filterOperations['endsWith'] = {
						text: findMessage.get('101721'),
						name: 'endsWith',
						symbol: 'endsWith'
					};
					break;
				}
				case 'textBox':
				case 'multiLanguageTextBox':
				case 'comboBox': {
					filterOperations['contains'] = {
						text: findMessage.get('101718'),
						name: 'contains',
						symbol: 'contains'
					};
					filterOperations['notContains'] = {
						text: findMessage.get('101719'),
						name: 'notContains',
						symbol: 'notContains'
					};
					filterOperations['startsWith'] = {
						text: findMessage.get('101720'),
						name: 'startsWith',
						symbol: 'startsWith'
					};
					filterOperations['endsWith'] = {
						text: findMessage.get('101721'),
						name: 'endsWith',
						symbol: 'endsWith'
					};
					filterOperations['equal'] = {
						text: findMessage.get('101711'),
						name: 'equal',
						symbol: '='
					};
					filterOperations['notEqual'] = {
						text: findMessage.get('101712'),
						name: 'notEqual',
						symbol: '<>'
					};
					break;
				}
				case 'actionButton':
					break;
				case 'timePicker':
				case 'datePicker':
				case 'dateTimePicker': {
					filterOperations['greaterThan'] = {
						text: findMessage.get('101713'),
						name: 'greaterThan',
						symbol: '>'
					};
					filterOperations['graterThenOrEqual'] = {
						text: findMessage.get('101714'),
						name: 'graterThenOrEqual',
						symbol: '>='
					};
					filterOperations['lessThan'] = {
						text: findMessage.get('101715'),
						name: 'lessThan',
						symbol: '<'
					};
					filterOperations['lessThanOrEqual'] = {
						text: findMessage.get('101716'),
						name: 'lessThanOrEqual',
						symbol: '=<'
					};
					break;
				}
				default: {
					filterOperations['contains'] = {
						text: findMessage.get('101718'),
						name: 'contains',
						symbol: 'contains'
					};
					filterOperations['notContains'] = {
						text: findMessage.get('101719'),
						name: 'notContains',
						symbol: 'notContains'
					};
					filterOperations['startsWith'] = {
						text: findMessage.get('101720'),
						name: 'startsWith',
						symbol: 'startsWith'
					};
					filterOperations['endsWith'] = {
						text: findMessage.get('101721'),
						name: 'endsWith',
						symbol: 'endsWith'
					};
					filterOperations['equal'] = {
						text: findMessage.get('101711'),
						name: 'equal',
						symbol: '='
					};
					filterOperations['notEqual'] = {
						text: findMessage.get('101712'),
						name: 'notEqual',
						symbol: '<>'
					};
					filterOperations['greaterThan'] = {
						text: findMessage.get('101713'),
						name: 'greaterThan',
						symbol: '>'
					};
					filterOperations['graterThenOrEqual'] = {
						text: findMessage.get('101714'),
						name: 'graterThenOrEqual',
						symbol: '>='
					};
					filterOperations['lessThan'] = {
						text: findMessage.get('101715'),
						name: 'lessThan',
						symbol: '<'
					};
					filterOperations['lessThanOrEqual'] = {
						text: findMessage.get('101716'),
						name: 'lessThanOrEqual',
						symbol: '=<'
					};
					break;
				}
			}

			const columnFiltering = this.__filtering.find(
				filter => filter.columnName === column.name
			);
			const columnSorting = this.__sorting.find(sort => sort.columnName == column.name);
			let filterChanged: boolean = false;
			let sortChanged: boolean = false;

			if (columnFiltering) {
				column.filtering.operation = columnFiltering.operation as any;
				column.filtering.value = columnFiltering.value;
				filterChanged = true;
			}
			if (columnSorting) {
				column.sorting.order = columnSorting.direction;
				sortChanged = true;
			}

			return { ...column, filterOperations, filterChanged, sortChanged };
		});
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			placeholder: this.placeholder.getData(),
			textAlign: this.textAlign,
			dataSource: this.dataSource,
			rows: this.rows,
			gridOptions: { ...this.gridOptions, columns: this.__getColumns() },
			allowClear: this.allowClear && !this.readOnly,
			sizeType: this.sizeType,
			valueType: this.valueType,
			text: this.text,
			showOnVisible: this.showOnVisible
		};
	}

	refreshLookupItems() {
		this.getLoadOptions(true);
	}

	async getLoadOptions(isFirst?: boolean) {
		if (
			this.dataSource &&
			!this.dataSource.runAtServer &&
			this.dataSource.name &&
			this.__form.serviceUrl
		) {
			const loadOptions = {
				filters:
					!isNullOrUndefined(this.__searchValue) && this.__searchValue != ''
						? [
								{
									Filters: this.dataSource?.displayExpr?.map(exp => {
										const type = this.gridOptions.columns.find(
											i => i.name == exp
										)?.dbType?.type;
										return {
											Column: exp,
											Not: false,
											Type: type == 'decimal' ? 1 : 6,
											Value: this.__searchValue,
											ItemType: 1
										};
									}),
									Operator: 2
								}
						  ]
						: [],
				pagination: { skip: 0, take: 20 },
				sorts: []
			};

			this.getDataSourceData(loadOptions)
				.then(response => {
					const data = FormControlsHelpers.getResultData(response.data);
					if (response.data?.success) {
						if (isFirst) {
							this.__proxyChanging.loading = true;
							this.nonSelectedItems = data;
							setTimeout(() => {
								this.__proxyChanging.loading = false;
							}, 1);
						} else {
							this.__proxyChanging.tempItems = data;
						}
					}
				})
				.catch(() => {
					this.__proxyChanging.tempItems = [];
				});
		}
	}

	getDataSourceControl() {
		return this.dataSource?.name && this.dataSource?.name !== '';
	}

	getLookupItems(): Array<IItem> {
		if (!this.getDataSourceControl() && this.value?.length) {
			const items = [];
			const spliteText = this.text.split(',');
			this.value.forEach((val, index) => {
				items.push({
					value: val,
					text: spliteText[index],
					key: val.toString()
				});
			});
			return items;
		}

		let datas = this.__searchValue ? [...this.tempItems] : [...this.nonSelectedItems];

		function getItemForCells(item) {
			return item.cells.reduce((acc, cell) => {
				const { name, value } = cell;
				if (name !== null && value !== null) {
					acc[name] = value;
				}
				return acc;
			}, {});
		}

		if (this.getDataSourceControl() && this.dataSource.runAtServer) {
			datas = this.rows?.map(row => getItemForCells(row));
		}

		if (this.selectedItems?.length) {
			const valueExpr = this.dataSource?.valueExpr;
			this.selectedItems.forEach(item => {
				const data = datas.find(d => d[valueExpr] == item[valueExpr]);
				if (!data) {
					datas.push(item);
				}
			});
		}

		if (!datas.length && !this.selectedItems?.length && this.nonSelectedItems?.length) {
			datas = this.nonSelectedItems;
		}

		if (datas?.length == 0) {
			if (this.value?.length) {
				const spliteText = this.text.split(',');
				const options = [];
				this.value.forEach((val, index) => {
					if (spliteText[index]) {
						options.push({
							value: val,
							text: spliteText[index],
							key: val.toString(),
							externalData: {
								data: null
							}
						});
					}
				});
				return options;
			}

			return [];
		}

		let data = datas || [];

		if (!this.dataSource.valueExpr) {
			return [];
		}

		let displayFormat = this.dataSource.displayFormat;

		if (displayFormat.includes('.__userCulture')) {
			displayFormat = displayFormat.replace(
				'.__userCulture',
				`['${this.__form.currentLanguage.culture}']`
			);
		}

		const dateColumns = this.gridOptions.columns.filter(
			c => c.dbType.type === 'date' || c.dbType.type === 'dateTime'
		);

		const getItem = (items: any) => {
			let item = items.cells ? getItemForCells(items) : items;

			if (dateColumns.length) {
				item = cloneDeep(item);

				dateColumns.forEach(c => {
					if (item[c.name] && c.format) {
						const date = dayjs(item[c.name]);

						if (date.isValid()) {
							item[c.name] = date.format(c.format);
						}
					}
				});
			}

			const textML = new MultiLanguageText();
			textML.setText(liquidEngine.parseAndRenderSync(displayFormat, item));
			const value = item[this.dataSource.valueExpr];
			return {
				value,
				text: textML.getText(),
				key: value?.toString(),
				externalData: {
					data: item
				}
			};
		};

		const options = data?.map(item => getItem(item));
		if (this.value?.length) {
			const spliteText = this.text.split(',');
			this.value.forEach((val, index) => {
				const item = options.find(i => i.value == val);
				if (!item && spliteText[index]) {
					options.push({
						value: val,
						text: spliteText[index],
						key: val.toString(),
						externalData: {
							data: null
						}
					});
				}
			});
		}

		return options;
	}

	onSelectedChange(args) {
		if (
			args.senderArgs?.[0] &&
			args.senderArgs?.length &&
			Object.keys(args.senderArgs?.[0])?.length
		) {
			const dataField = this.dataSource?.valueExpr;
			if (this.gridOptions?.selection?.mode == 'multiple') {
				let text = '';
				const value = [];
				const items = this.selectedItems?.length ? this.selectedItems : [];
				args.senderArgs[0].forEach(data => {
					if (Object.keys(data)?.length) {
						value.push(data.value);
						text = text === '' ? data.title : text + ',' + data.title;
						if (
							this.getDataSourceControl() &&
							items.findIndex(r => r[dataField] == data.value) == -1
						) {
							const item = this.tempItems.length
								? this.tempItems.find(r => r[dataField] == data.value)
								: this.nonSelectedItems.find(r => r[dataField] == data.value);

							if (item) items.push(item);
						}
					}
				});

				const tId = this.__form.beginTransaction();
				this.__commitIdFixed = true;
				this.__proxyChanging.selectedItems = items.filter(
					item => value.indexOf(item[dataField]) > -1
				);
				this.__form.endTransaction(tId).then(() => {
					this.__commitIdFixed = false;
					this.__proxyChanging.text = text;
					this.__proxyChanging.value = value;
				});
			} else {
				this.__proxyChanging.selectedItems = this.tempItems.length
					? this.tempItems.filter(r => r[dataField] == args.senderArgs[0].value)
					: this.nonSelectedItems.filter(r => r[dataField] == args.senderArgs[0].value);
				this.__form.applyChanges().then(() => {
					this.__proxyChanging.text = args.senderArgs[0].title;
					this.__proxyChanging.value = [args.senderArgs[0].value];
				});
			}
		} else {
			this.__proxyChanging.selectedItems = [];
			this.__proxyChanging.value = null;
			this.__proxyChanging.text = null;
		}
	}

	__checkIncreaseCommitId(path: string[]) {
		return this.__commitIdFixed;
	}

	__renderPopupContainer() {
		if (!this.__form.getShadowDomMode()) {
			return (
				<Portal>
					<div id={pickerPopupId}></div>
				</Portal>
			);
		}
	}

	__renderInput() {
		const inputText = this.text?.split(',').join(', ');
		if (
			!(
				this.__form.__fromMobile &&
				this.__form.__mobileFormView === Mobile.MobileFormView.NativePlus
			)
		) {
			const onSearch = text => {
				this.__searchValue = text;
				if (!isNullOrUndefined(text) && text !== '') {
					this.getLoadOptions();
				} else {
					this.__proxyChanging.tempItems = [];
				}
			};

			const mode = this.gridOptions?.selection?.mode == 'multiple' ? 'multiple' : undefined;
			return (
				<>
					{this.__renderPopupContainer()}
					<BCMultipleCombobox
						showSearch
						style={this.__getInputStyle()}
						options={this.getLookupItems()}
						className={Styles.lookupStyle}
						value={this.value?.length > 0 ? this.value : []}
						onSelectedChange={this.onSelectedChange}
						allowClear={this.allowClear}
						placeholder={this.placeholder.getText()}
						disabled={this.__isDisabled() || this.__isReadOnly()}
						readonly={this.__isReadOnly()}
						size={this.__getSizeType()}
						onBlur={() => {
							this.__searchValue = null;
							this.__proxyChanging.tempItems = [];
						}}
						onSearch={onSearch}
						mode={mode}
						dropdownClassName={Styles.multipleComboboxDropdown}
						getPopupContainer={() =>
							this.__form.__getShadowPopupContainer(pickerPopupId)
						}
						extractPopupKey={this.__form.getShadowDomMode() && pickerPopupId}
					/>
				</>
			);
		} else {
			return (
				<BCInput
					style={this.__getInputStyle()}
					className={Styles.lookupStyle}
					readOnly
					disabled={this.__isDisabled()}
					value={inputText}
					allowClear={this.allowClear}
					onChange={this.onClear.bind(this)}
					placeHolder={this.placeholder.getText()}
					textAlign={this.textAlign}
				/>
			);
		}
	}

	__getSizeType() {
		return this.sizeType ?? this.__form.sizeType;
	}

	__render(children?: JSX.Element) {
		return super.__render(
			<>
				<BCSpaceCompact
					block
					size={this.__getSizeType()}
				>
					{this.__renderInput()}
					{this.__renderButtons()}
				</BCSpaceCompact>
			</>
		);
	}
}
